import { BrowserModule } from "@angular/platform-browser";
import { CommonModule } from "@angular/common";
import { DatePipe } from '@angular/common';
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpClientModule } from "@angular/common/http";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AngularEditorModule } from "@kolkov/angular-editor";
import { NgxPaginationModule } from "ngx-pagination";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { TrackCapsDirective } from "./app.component";
import { DynamicFormComponent } from "./dynamic-form/dynamic-form.component";
import { SignInComponent } from "./sign-in/sign-in.component";
import { LoginComponent } from "./sign-in/login/login.component";
import { ResetPasswordEmailComponent } from "./sign-in/reset-password/reset-password-email.component";
import { ResetPasswordEmailSentComponent } from "./sign-in/reset-password-completed/reset-password-email-sent.component";
import { ResetPasswordContactComponent } from "./sign-in/reset-password-get-help/reset-password-contact.component";
import { DynamicFormDataEntryComponent } from "./dynamic-form-data-entry/dynamic-form-data-entry.component";
import { DynamicGridComponent } from "./dynamic-grid/dynamic-grid.component";
import { DynamicViewComponent } from "./dynamic-view/dynamic-view.component";
import { DynamicEditComponent } from "./dynamic-edit/dynamic-edit.component";
import { PaymentComponent } from "./payment/payment.component";
import { PaymentChoosePlanComponent } from "./payment/payment-choose-plan/payment-choose-plan.component";
import { PaymentChooseProfessionalComponent } from "./payment/payment-choose-professional/payment-choose-professional.component";
import { PaymentMonthlySubscriptionComponent } from "./payment/payment-monthly-subscription/payment-monthly-subscription.component";
import { RequestAccessComponent } from "./request-access/request-access.component";
import { FriendInvitationComponent } from "./friend-invitation/friend-invitation.component";
import { MyDashboardComponent } from "./my-dashboard/my-dashboard.component";
import { MyDelegatesComponent } from "./my-delegates/my-delegates.component";
import { SharedPlansComponent } from "./shared-plans/shared-plans.component";
import { SharedPlanSummaryComponent } from "./shared-plans/shared-plan-summary/shared-plan-summary.component";
import { SharedPlanExplanationComponent } from "./shared-plans/shared-plan-explanation/shared-plan-explanation.component";
import { DialogComponent } from "./dialog/dialog.component";
import { SignUpComponent } from "./sign-up/sign-up.component";
import { SignUpReferralCodeComponent } from "./sign-up/sign-up-referral-code/sign-up-referral-code.component";
import { SignUpPasswordComponent } from "./sign-up/sign-up-password/sign-up-password.component";
import { SignUpCompletedComponent } from "./sign-up/sign-up-completed/sign-up-completed.component";
import { DelegateHomePageComponent } from "./delegate-home-page/delegate-home-page.component";
import { AdminDashboardComponent } from "./admin-dashboard/admin-dashboard.component";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { NgxMaskModule, IConfig } from "ngx-mask";
import { DelegateInvitationSentComponent } from "./my-delegates/delegate_invitation_sent/delegate-invitation-sent.component";
import { ChartsModule } from "ng2-charts";
import { TopNavComponent } from "./top-nav/top-nav.component";
import { DelegateHelpers } from "./helpers/delegate-helpers";
import { from } from "rxjs";
import { CharityInvitationSentComponent } from "./friend-invitation/charity-invitation-sent/charity-invitation-sent.component";
import { AccountSettingsComponent } from "./account-settings/account-settings.component";
import { RequestAccessPlanComponent } from "./request-access/request-access-plan/request-access-plan.component";
import { RequestSubmittedComponent } from "./request-access/request-submitted/request-submitted.component";
import { FooterComponent } from "./footer/footer.component";
import { FinancialSummariesComponent } from "./financial-summaries/financial-summaries.component";
import { FinancialSummariesNetWorthComponent } from "./financial-summaries-net-worth/financial-summaries-net-worth.component";
import { FinancialSummariesNetIncomeComponent } from "./financial-summaries-net-income/financial-summaries-net-income.component";
import { FinancialSummariesSectionItemsComponent } from "./financial-summaries-section-items/financial-summaries-section-items.component";
import { FinancialSummariesItemsComponent } from "./financial-summaries-items/financial-summaries-items.component";
import { DelegateSummaryComponent } from "./my-delegates/delegate-summary/delegate-summary.component";
import { DelegateExplanationComponent } from "./my-delegates/delegate-explanation/delegate-explanation.component";
import { NotificationBubbleComponent } from "./notification-bubble/notification-bubble.component";
import { DeletedItemsComponent } from "./deleted-items/deleted-items.component";
import { PlanReviewComponent } from "./plan-review/plan-review.component";
import { DelegateRightsComponent } from "./delegate-rights/delegate-rights.component";
import { SectionBoxComponent } from "./section-box/section-box.component";
import { CategoryBoxComponent } from "./category-box/category-box.component";
import { ViewFormArrayComponent } from "./view-form-array/view-form-array.component";
import { MultiCheckboxComponent } from "./multi-checkbox/multi-checkbox.component";
import { ViewBoxComponent } from "./view-box/view-box.component";
import { DelegateExplanationMobileComponent } from "./my-delegates/delegate-explanation-mobile/delegate-explanation-mobile.component";
import { SectionViewBoxComponent } from "./section-view-box/section-view-box.component";
import { SharedPlanExplanationMobileComponent } from "./shared-plans/shared-plan-explanation-mobile/shared-plan-explanation-mobile.component";
import { PropertyEquityGainComponent } from "./property-components/property-equity-gain/property-equity-gain.component";
import { PropertyRentalIncomeExpenseComponent } from "./property-components/property-rental-income-expense/property-rental-income-expense.component";
import { BreadcrumbComponent } from "./breadcrumb/breadcrumb.component";
import { FinancialSummariesPropertiesComponent } from "./financial-summaries-properties/financial-summaries-properties.component";
import { DynamicBreadcrumbComponent } from "./dynamic-breadcrumb/dynamic-breadcrumb.component";
import { DynamicInputComponent } from "./dynamic-input/dynamic-input.component";
import { DynamicDropdownComponent } from "./dynamic-dropdown/dynamic-dropdown.component";
import { ProfessionalHomePageComponent } from "./professional-home-page/professional-home-page.component";
import { BreadcrumbService } from "./services/breadcrumb.service";
import { PreviousUrlService } from "./services/previous-url.service";
import { LoaderComponent } from "./loader/loader.component";
import { BnNgIdleService } from "bn-ng-idle";
import { AccountsComponent } from "./admin-dashboard/accounts/accounts.component";
import { ProfessionalsComponent } from "./admin-dashboard/professionals/professionals.component";
import { PartnersComponent } from "./admin-dashboard/partners/partners.component";
import { AnalyticsComponent } from "./admin-dashboard/analytics/analytics.component";
import { ListsComponent } from "./admin-dashboard/lists/lists.component";
import { MonitoringComponent } from "./admin-dashboard/monitoring/monitoring.component";
import { CopyEditorComponent } from "./admin-dashboard/copy-editor/copy-editor.component";
import { RolesComponent } from "./admin-dashboard/roles/roles.component";
import { ExcelService } from "./services/excel.service";
import { AdminDashboardUserDetailsComponent } from "./admin-dashboard-user-details/admin-dashboard-user-details.component";
import { AccountDetailsComponent } from "./admin-dashboard-user-details/account-details/account-details.component";
import { ProfessionalsDetailsComponent } from "./admin-dashboard-user-details/professionals-details/professionals-details.component";
import { PartnersDetailsComponent } from "./admin-dashboard-user-details/partners-details/partners-details.component";
import { RouterModule } from "@angular/router";
import { FileUploadComponent } from "./file-upload/file-upload.component";
import { DelegateEmptyStateComponent } from "./my-delegates/delegate-empty-state/delegate-empty-state.component";
import { ResetPasswordComponent } from "./reset-password/reset-password.component";
import { SponsorPageComponent } from "./sponsor-page/sponsor-page.component";
import { FileAttachmentComponent } from "./file-upload/file-attachment/file-attachment.component";
import { DeletedItemsMobileExplanationComponent } from "./deleted-items/deleted-items-mobile-explanation/deleted-items-mobile-explanation.component";
import { DelegateHomeMobileExplanationComponent } from "./delegate-home-page/delegate-home-mobile-explanation/delegate-home-mobile-explanation.component";
import { SuggestedNextStepsComponent } from "./suggested-next-steps/suggested-next-steps.component";
import { MailRulesComponent } from "./admin-dashboard/mail-rules/mail-rules.component";
import { SuggestionRulesComponent } from "./admin-dashboard/suggestion-rules/suggestion-rules.component";
import { GlobalModalComponent } from "./global-modal/global-modal.component";
import { UserIdleModule } from "angular-user-idle";
import { ProfessionalManagerHomePageComponent } from "./professional-manager-home-page/professional-manager-home-page.component";
import { SecurityPageComponent } from "./security-page/security-page.component";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { MailLogComponent } from "./admin-dashboard/mail-log/mail-log.component";
import { MailLogDetailsComponent } from "./admin-dashboard-user-details/mail-log-details/mail-log-details.component";
import { PaymentSubcriptionComponent } from "./payment-subcription/payment-subcription.component";
import { ResourcesComponent } from "./admin-dashboard/resources/resources.component";
import { EventsLogComponent } from "./events-log/events-log.component";
import { AgGridModule } from "ag-grid-angular";
import { EventLogWindowComponent } from "./event-log-window/event-log-window.component";
import { EventsPaneComponent } from "./events-pane/events-pane.component";
import { EventsPaneContainer } from "./events-pane-container/events-pane-container.component";
import { RefcodesComponent } from "./admin-dashboard/refcodes/refcodes.component";
import { ProfessionalManagerAccountDetailsComponent } from "./professional-manager-account-details/professional-manager-account-details.component";
import { ScrollTopComponent } from "./scroll-top/scroll-top.component";
import { UnlockRequestComponent } from "./admin-dashboard/unlock-request/unlock-request.component";
import { UnlockRequestDetailsComponent } from "./admin-dashboard-user-details/unlock-request-details/unlock-request-details.component";
import { MailQueueComponent } from "./admin-dashboard/mail-queue/mail-queue.component";
import { HeaderComponent } from "./header/header.component";
import { DynamicCurrencyComponent } from "./dynamic-currency/dynamic-currency.component";
import { SecurityPageSignUpComponent } from './security-page-sign-up/security-page-sign-up.component';
import { DynamicJointOwnershipComponent } from './dynamic-joint-ownership/dynamic-joint-ownership.component';
import { TimeagoModule } from 'ngx-timeago';
import { Ng2SearchPipeModule } from 'ng2-search-filter';
import { SearchPipe } from "./search.pipe";
import { XTestingComponent } from './x-testing/x-testing.component';
import { AddClientsComponent } from './professional-home-page/add-clients/add-clients.component';
import { StepsHeaderComponent } from './professional-home-page/steps-header/steps-header.component';
import { UploadProfessionalsListComponent } from './professional-home-page/upload-professionals-list/upload-professionals-list.component';
import { EmailTemplateComponent } from './professional-home-page/email-template/email-template.component';
import { EmailPreviewComponent } from './professional-home-page/email-preview/email-preview.component';
import { ModalInviteComponent } from './professional-home-page/modal-invite/modal-invite.component';
import { InviteOnlyComponent } from './professional-home-page/invite-only/invite-only.component';

// import { NgxFlagPickerModule } from ''

export const options: Partial<IConfig> | (() => Partial<IConfig>) = {};

@NgModule({
  declarations: [
    AppComponent,
    DynamicFormComponent,
    SignInComponent,
    LoginComponent,
    ResetPasswordEmailComponent,
    ResetPasswordEmailSentComponent,
    ResetPasswordContactComponent,
    DynamicFormDataEntryComponent,
    DynamicGridComponent,
    DynamicViewComponent,
    DynamicEditComponent,
    PaymentComponent,
    PaymentChoosePlanComponent,
    PaymentChooseProfessionalComponent,
    PaymentMonthlySubscriptionComponent,
    RequestAccessComponent,
    FriendInvitationComponent,
    MyDashboardComponent,
    MyDelegatesComponent,
    SharedPlansComponent,
    TrackCapsDirective,
    DialogComponent,
    SignUpComponent,
    SignUpReferralCodeComponent,
    SignUpPasswordComponent,
    SignUpCompletedComponent,
    DelegateHomePageComponent,
    AdminDashboardComponent,
    DelegateInvitationSentComponent,
    TopNavComponent,
    CharityInvitationSentComponent,
    AccountSettingsComponent,
    RequestAccessPlanComponent,
    RequestSubmittedComponent,
    FooterComponent,
    FinancialSummariesComponent,
    FinancialSummariesNetWorthComponent,
    FinancialSummariesNetIncomeComponent,
    FinancialSummariesSectionItemsComponent,
    FinancialSummariesItemsComponent,
    DelegateSummaryComponent,
    DelegateExplanationComponent,
    SharedPlanSummaryComponent,
    SharedPlanExplanationComponent,
    NotificationBubbleComponent,
    DeletedItemsComponent,
    PlanReviewComponent,
    DelegateRightsComponent,
    SectionBoxComponent,
    CategoryBoxComponent,
    ViewFormArrayComponent,
    MultiCheckboxComponent,
    ViewBoxComponent,
    DelegateExplanationMobileComponent,
    SectionViewBoxComponent,
    SharedPlanExplanationMobileComponent,
    PropertyEquityGainComponent,
    PropertyRentalIncomeExpenseComponent,
    BreadcrumbComponent,
    FinancialSummariesPropertiesComponent,
    DynamicBreadcrumbComponent,
    DynamicInputComponent,
    DynamicDropdownComponent,
    ProfessionalHomePageComponent,
    LoaderComponent,
    AccountsComponent,
    ProfessionalsComponent,
    PartnersComponent,
    AnalyticsComponent,
    ListsComponent,
    MonitoringComponent,
    CopyEditorComponent,
    RolesComponent,
    AdminDashboardUserDetailsComponent,
    AccountDetailsComponent,
    ProfessionalsDetailsComponent,
    PartnersDetailsComponent,
    FileUploadComponent,
    DelegateEmptyStateComponent,
    ResetPasswordComponent,
    SponsorPageComponent,
    FileAttachmentComponent,
    DeletedItemsMobileExplanationComponent,
    DelegateHomeMobileExplanationComponent,
    SuggestedNextStepsComponent,
    MailRulesComponent,
    SuggestionRulesComponent,
    GlobalModalComponent,
    ProfessionalManagerHomePageComponent,
    SecurityPageComponent,
    MailLogComponent,
    MailLogDetailsComponent,
    PaymentSubcriptionComponent,
    ResourcesComponent,
    EventsLogComponent,
    EventLogWindowComponent,
    EventsPaneComponent,
    EventsPaneContainer,
    RefcodesComponent,
    ProfessionalManagerAccountDetailsComponent,
    ScrollTopComponent,
    UnlockRequestComponent,
    UnlockRequestDetailsComponent,
    MailQueueComponent,
    HeaderComponent,
    DynamicCurrencyComponent,
    SecurityPageSignUpComponent,
    DynamicJointOwnershipComponent,
    SearchPipe,
    XTestingComponent,
    AddClientsComponent,
    StepsHeaderComponent,
    UploadProfessionalsListComponent,
    EmailTemplateComponent,
    EmailPreviewComponent,
    ModalInviteComponent,
    InviteOnlyComponent
  ],
  imports: [
    BrowserModule,
    RouterModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    HttpClientModule,
    CommonModule,
    BrowserAnimationsModule,
    NgbModule,
    NgxMaskModule.forRoot(options),
    UserIdleModule.forRoot({ idle: 900, timeout: 300, ping: 120 }),
    ChartsModule,
    DragDropModule,
    AngularEditorModule,
    AgGridModule,
    NgxPaginationModule,
    TimeagoModule.forRoot(),
    Ng2SearchPipeModule
  ],

  providers: [
    DelegateHelpers,
    BreadcrumbService,
    PreviousUrlService,
    BnNgIdleService,
    ExcelService,
    DatePipe
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
